// External imports
import React, { useEffect, useRef, useState } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
// Internal imports
import AuthAPI from '~/app/api/authAPI.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import Spinner from '#/Common/Spinner.jsx';
import WebHelpers, { statusOK } from '~/app/webHelpers.js';
import { useFinalizeLoginFlow, useSetState } from '~/app/Utility/customHooks';
import { webSocket } from '~/app/Utility/socketConnection.js';

import { AUTH_STAGE, LOGIN_SETTINGS, SNACKBAR_SEVERITY, SPINNER_DIALOGS } from '~/app/constants';
import {
  SSO_ACCOUNT_PAGE_SIZE_OPTIONS,
  SSO_COLUMNS,
  SSO_PRE_AUTH_TOKEN_KEY,
} from '~/app/Pages/SSOAccountPicker/helpers/ssoConstants';
import {
  compareTextSortValues,
  expirePickerSession,
  toLastUsedTimestamp,
  extractSSOPreAuthTokenFromHash,
  normalizeSSOAccounts,
  renderSSOAccountPickerColumnGroup,
} from '~/app/Pages/SSOAccountPicker/helpers/ssoHelpers';
// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { actions as appActions } from '~/app/redux/modules/app';
import { actions as loginSetupActions } from '~/app/redux/loginSetup/duck';
// Styling imports
import { withStyles } from '@material-ui/core/styles';
import { styles } from '~/app/Pages/SSOAccountPicker/css/accountPickerStyles.js';

/**
 * SSO account picker screen.
 *
 * Reads the pre-auth token from the callback hash, loads the linked CLO accounts,
 * supports debounced server-backed search + pagination, and lets the user choose
 * a login (or auto-log in when only one account is available).
 *
 * @component
 * @category SSO
 * @see CLO-11566
 */

const SSOAccountPicker = props => {
  const SSO_PICKER_SESSION_TIMEOUT_SECONDS = 5 * 60;

  // Header-cell IDs are reused via `headers` on body cells because the visual
  // header and scrollable body are rendered as separate tables.
  const SSO_TABLE_HEADER_IDS = {
    name: 'sso-col-name',
    role: 'sso-col-role',
    office: 'sso-col-office',
    user_id: 'sso-col-user-id',
    account_code: 'sso-col-account-code',
    efin: 'sso-col-efin',
    last_used: 'sso-col-last-used',
    action: 'sso-col-action',
  };

  const { classes } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const latestRequestID = useRef(0);
  // Tracks the latest login attempt so stale async completions are ignored.
  const latestLoginRequestID = useRef(0);
  // Holds the active login request cancel fn for unmount/back/superseded cleanup.
  const cancelLoginRequest = useRef(null);
  // Route liveness guard: prevents state/storage/navigation writes after unmount.
  const isPickerActive = useRef(true);
  const previousFetchCriteria = useRef(null);
  const pickerSessionExpiryEpochSeconds = useRef(null);
  const hasHandledSessionExpiry = useRef(false);
  const { isAuthenticated, isAuthenticating, authStage } = useSelector(state => ({
    isAuthenticated: state.app.isAuthenticated,
    isAuthenticating: state.loginSetup.isAuthenticating,
    authStage: state.app.authStage,
  }));

  const [tokenReady, setTokenReady] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sessionSecondsRemaining, setSessionSecondsRemaining] = useState(null);

  const [state, setState] = useSetState({
    accounts: [],
    searchInput: '',
    isSearchLoading: false,
    isSearchDebouncing: false,
    sortKey: 'last_used',
    isSortAsc: false,
    isLoading: true,
    isAutoLoggingIn: false,
    isAccountsDataLoaded: false,
    isSubmitting: false,
    page: 0,
    rowsPerPage: 14,
    totalRows: 0,
  });

  // Reuses the same finalize lifecycle used by other SSO interstitial pages.
  // This keeps account-picker-specific side effects in callbacks while the
  // auth-cycle timing logic remains centralized in one hook.
  const { startFinalize } = useFinalizeLoginFlow({
    isAuthenticated,
    isAuthenticating,
    isAwaitPickOffice: authStage === AUTH_STAGE.AWAIT_PICK_OFFICE,
    onStart: () => {
      dispatch(loginSetupActions.onFinalizeLogin());
    },
    onSuccess: () => {
      // Finalize completed and global auth is established; unlock picker UI
      // state and continue with standard dashboard routing.
      stopPickerTimer();
      setState({ isSubmitting: false, isAutoLoggingIn: false });
      webSocket.services();
      history.push({ pathname: '/dashboard' });
    },
    onAwaitPickOffice: () => {
      // Expected branch for preparers lacking an active office assignment.
      stopPickerTimer();
      setState({ isSubmitting: false, isAutoLoggingIn: false });
      history.push({ pathname: '/database' });
    },
    onFailure: () => {
      // Finalize cycle ended without a success branch; clear session artifacts
      // and send user back to login to restart SSO cleanly.
      setState({ isSubmitting: false, isAutoLoggingIn: false });
      WebHelpers.cleanupSession();
      WebHelpers.cleanupLocalStorage();
      dispatch(appActions.onLogout());
      ErrorHelpers.handleError(
        'SSO Login Error',
        ErrorHelpers.createSimpleError('Unable to finalize sign in. Please sign in again.'),
      );
      history.push({ pathname: '/' });
    },
  });

  const formatSessionCountdown = remainingSeconds => {
    if (remainingSeconds == null) {
      return '';
    }

    const safeSeconds = Math.max(0, remainingSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const handlePickerSessionExpired = () => {
    expirePickerSession({
      isPickerActiveRef: isPickerActive,
      hasHandledSessionExpiryRef: hasHandledSessionExpiry,
      latestRequestIDRef: latestRequestID,
      latestLoginRequestIDRef: latestLoginRequestID,
      cancelLoginRequestRef: cancelLoginRequest,
      dispatch,
      history,
    });
  };

  // Disarm the pre-auth timer once a successful flow is navigating away.
  const stopPickerTimer = () => {
    pickerSessionExpiryEpochSeconds.current = null;
    hasHandledSessionExpiry.current = true;
    setSessionSecondsRemaining(null);
  };

  /**
   * Initializes picker auth context from callback hash token, with
   * sessionStorage fallback for refreshes after hash cleanup.
   */
  useEffect(() => {
    const token =
      extractSSOPreAuthTokenFromHash(window.location.hash) ||
      sessionStorage.getItem(SSO_PRE_AUTH_TOKEN_KEY);

    if (!token) {
      // Back navigation can restore this route after a successful login when
      // the one-time pre-auth token has already been removed. Preserve the
      // established session instead of treating it as picker expiry.
      if (isAuthenticated) {
        history.replace({ pathname: '/dashboard' });
        return;
      }

      // Tokenless access to picker is treated as an expired/invalid SSO session.
      handlePickerSessionExpired();
      return;
    }

    let tokenExp;
    try {
      tokenExp = Number(jwtDecode(token)?.exp) || 0;
    } catch (_) {
      handlePickerSessionExpired();
      return;
    }

    sessionStorage.setItem(SSO_PRE_AUTH_TOKEN_KEY, token);
    const nowSeconds = Math.floor(Date.now() / 1000);
    const cappedExpiry = nowSeconds + SSO_PICKER_SESSION_TIMEOUT_SECONDS;
    pickerSessionExpiryEpochSeconds.current =
      tokenExp > 0 ? Math.min(tokenExp, cappedExpiry) : cappedExpiry;

    if (pickerSessionExpiryEpochSeconds.current != null) {
      setSessionSecondsRemaining(Math.max(0, pickerSessionExpiryEpochSeconds.current - nowSeconds));
    } else {
      setSessionSecondsRemaining(null);
    }

    history.replace({ pathname: '/sso-account-picker', search: '', hash: '' });
    setTokenReady(true);
  }, []);

  useEffect(() => {
    if (!tokenReady || pickerSessionExpiryEpochSeconds.current == null) {
      return;
    }

    const updateCountdown = () => {
      if (pickerSessionExpiryEpochSeconds.current == null) {
        return;
      }

      const nowSeconds = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, pickerSessionExpiryEpochSeconds.current - nowSeconds);
      setSessionSecondsRemaining(remaining);

      if (remaining <= 0) {
        handlePickerSessionExpired();
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [tokenReady]);

  useEffect(() => {
    if (state.isSubmitting) {
      return;
    }

    if (state.searchInput === debouncedSearchTerm) {
      if (state.isSearchDebouncing) {
        setState({ isSearchDebouncing: false });
      }
      return;
    }

    setState({ isSearchDebouncing: true });
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(state.searchInput);
      setState({ isSearchDebouncing: false, page: 0 });
    }, 300);

    return () => clearTimeout(timer);
  }, [state.searchInput, state.isSubmitting]);

  useEffect(() => {
    if (!tokenReady || state.isSubmitting) {
      return;
    }

    const criteria = {
      page: state.page,
      rowsPerPage: state.rowsPerPage,
      searchTerm: debouncedSearchTerm,
    };

    const isSearchFetch =
      previousFetchCriteria.current !== null &&
      previousFetchCriteria.current.searchTerm !== criteria.searchTerm;

    previousFetchCriteria.current = criteria;

    loadSSOAccounts({
      page: state.page,
      rowsPerPage: state.rowsPerPage,
      searchTerm: debouncedSearchTerm,
      fetchMode: isSearchFetch ? 'search' : 'table',
    });
  }, [tokenReady, state.page, state.rowsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    // Ignore any in-flight account fetch responses after picker unmount.
    // This prevents stale async callbacks from navigating after the user leaves.
    return () => {
      isPickerActive.current = false;
      latestRequestID.current += 1;
      latestLoginRequestID.current += 1;
      if (cancelLoginRequest.current) {
        cancelLoginRequest.current('SSO account login request was canceled');
        cancelLoginRequest.current = null;
      }
    };
  }, []);

  /**
   * Loads a page of SSO accounts from the backend.
   * Uses request IDs to ignore stale responses when users type quickly.
   *
   * @param {{page: number, rowsPerPage: number, searchTerm: string, fetchMode: 'search'|'table'}} params
   * Fetch parameters and UI mode.
   * @returns {Promise<void>} Resolves after state updates complete.
   */
  const loadSSOAccounts = async ({ page, rowsPerPage, searchTerm, fetchMode }) => {
    const requestID = latestRequestID.current + 1;
    latestRequestID.current = requestID;

    try {
      if (fetchMode === 'search') {
        setState({ isSearchLoading: true });
      } else {
        setState({ isLoading: true, isSearchLoading: false });
      }

      const res = await AuthAPI.fetchSSOAccounts({
        page: page + 1,
        perPage: rowsPerPage,
        search: searchTerm,
      });

      if (requestID !== latestRequestID.current) {
        return;
      }

      if (statusOK(res)) {
        const accounts = res.data?.accounts || [];
        const totalRows = Number(res.data?.total) || 0;

        if (!accounts.length && totalRows > 0 && page > 0) {
          setState({ isLoading: false, isSearchLoading: false, page: page - 1 });
          return;
        }

        if (totalRows === 0 && !searchTerm) {
          sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
          history.push({ pathname: '/sso-no-account' });
          return;
        }

        const normalizedAccounts = normalizeSSOAccounts(accounts);

        // First-time SSO users with one mapped login should not need to use the picker.
        // TODO(Phase 2): both email and IdP-username sign-ins land here today with no
        // way to prefer a specific login when multiple are mapped to the same identity;
        // revisit once per-login resolution is possible again.
        if (totalRows === 1 && page === 0 && !searchTerm && normalizedAccounts.length === 1) {
          setState({ isAccountsDataLoaded: true });
          const autoLoginSucceeded = await loginAsSSOAccount(normalizedAccounts[0], true);
          // Auto-login may complete after navigation/unmount; avoid writing stale
          // picker state when this route is no longer active.
          if (!isPickerActive.current || autoLoginSucceeded) {
            return;
          }
        }

        // Populate the picker table and wait for explicit user selection.
        setState({
          accounts: normalizedAccounts,
          totalRows,
          isLoading: false,
          isSearchLoading: false,
          isAccountsDataLoaded: true,
        });
        return;
      }

      setState({ isLoading: false, isSearchLoading: false });

      // 401 means the picker token has expired. Redirect to login with a clear message
      // rather than showing a generic error or sending to the no-account page.
      if (res?.status === 401) {
        handlePickerSessionExpired();
        return;
      }

      ErrorHelpers.handleError(
        'Unable to load SSO accounts',
        ErrorHelpers.createSimpleError('Unexpected response from the server. Please try again.'),
      );
      sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
      history.push({ pathname: '/' });
    } catch (error) {
      if (requestID !== latestRequestID.current) {
        return;
      }

      setState({ isLoading: false, isSearchLoading: false });

      // Network-level errors may also surface as expired token rejections.
      const isExpired =
        error?.response?.status === 401 ||
        String(error?.message || '')
          .toLowerCase()
          .includes('expired');

      if (isExpired) {
        handlePickerSessionExpired();
        return;
      }

      ErrorHelpers.handleError(
        'Unable to load SSO accounts',
        ErrorHelpers.createSimpleError(
          'If this problem persists, please contact technical support.',
        ),
      );
      sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
      history.push({ pathname: '/' });
    }
  };

  /**
   * Updates the raw search input value.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Search input change event.
   */
  const handleSearchChange = e => {
    const value = e.target.value;
    setState({ searchInput: value });
  };

  /**
   * Returns the current page rows sorted by the selected column/order.
   * SSO-TODO: Move sorting to the backend request so pagination and sorting
   * share one global ordering across the full result set.
   * Current behavior sorts only the currently loaded page.
   *
   * @returns {Object[]} Sorted account rows for rendering.
   */
  const getFilteredAccounts = () => {
    const list = [...state.accounts];

    if (state.sortKey) {
      list.sort((a, b) => {
        if (state.sortKey === 'last_used') {
          const aTime = toLastUsedTimestamp(a.last_used);
          const bTime = toLastUsedTimestamp(b.last_used);
          const aMissing = aTime == null;
          const bMissing = bTime == null;

          if (aMissing && bMissing) return 0;
          if (aMissing) return 1;
          if (bMissing) return -1;

          return state.isSortAsc ? aTime - bTime : bTime - aTime;
        }

        return compareTextSortValues(a[state.sortKey], b[state.sortKey], state.isSortAsc);
      });
    }
    return list;
  };

  /**
   * Handles pagination page changes.
   *
   * @param {Object} event Material UI pagination event.
   * @param {number} page Zero-based page index.
   */
  const handleChangePage = (event, page) => {
    setState({ page });
  };

  /**
   * Handles page-size changes and resets to first page.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event Rows-per-page event.
   */
  const handleChangeRowsPerPage = event => {
    setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
  };

  /**
   * Toggles sorting for a table column.
   *
   * @param {string} key Column key to sort by.
   */
  const handleSort = key => {
    setState(
      state.sortKey === key ? { isSortAsc: !state.isSortAsc } : { sortKey: key, isSortAsc: true },
    );
  };

  /**
   * Finalizes post-SSO routing (TOC, IRS message, or dashboard).
   *
   * @param {string} loginName The login name of the authenticated user.
   */
  const onLoginAsSSOSuccess = loginName => {
    dispatch(
      appActions.showSnackbarMessage(
        `Successfully logged in with SSO, ${loginName}.`,
        SNACKBAR_SEVERITY.SUCCESS,
        2500,
      ),
    );

    const showTOC = sessionStorage.getItem(LOGIN_SETTINGS.SHOW_TERMS_AND_CONDITIONS);
    const showMsg = sessionStorage.getItem(LOGIN_SETTINGS.SHOW_IRS_MESSAGE);

    if (showTOC === 'true') {
      stopPickerTimer();
      dispatch(appActions.setAuthStageAwaitTermsAndConditions());
      history.push({ pathname: '/terms-and-conditions' });
      return;
    }

    if (showMsg === 'true') {
      stopPickerTimer();
      dispatch(appActions.setAuthStageAwaitIRSMessage());
      history.push({ pathname: '/irs-message' });
      return;
    }

    // Do not expire here: finalize still runs while picker is mounted.
    // Expiry is applied in finalize success callbacks when navigation occurs.
    startFinalize();
  };

  /**
   * Performs login for a selected SSO account row.
   *
   * @param {Object} account Selected normalized account row.
   * @param {boolean} [autoLogin=false] True when using single-account auto-login flow.
   * @returns {Promise<boolean>} True when login succeeded.
   */
  const loginAsSSOAccount = async (account, autoLogin = false) => {
    if (state.isSubmitting || !isPickerActive.current) {
      return false;
    }

    // Invalidate active/queued account list requests so stale failures cannot
    // override an in-progress login attempt.
    latestRequestID.current += 1;
    // Every login attempt gets a monotonic ID so only the newest completion wins.
    const requestID = latestLoginRequestID.current + 1;
    latestLoginRequestID.current = requestID;

    // Cancel any prior login request before starting a newer attempt.
    if (cancelLoginRequest.current) {
      cancelLoginRequest.current('Superseded by newer SSO account login request');
      cancelLoginRequest.current = null;
    }
    const source = axios.CancelToken.source();
    cancelLoginRequest.current = source.cancel;

    setState({ isSubmitting: true, isAutoLoggingIn: autoLogin });

    try {
      const res = await AuthAPI.loginAsSSO(account.name, { cancelToken: source.token });

      // Ignore late completions after unmount or after a newer request starts.
      if (!isPickerActive.current || requestID !== latestLoginRequestID.current) {
        return false;
      }

      if (res === false) {
        stopPickerTimer();
        // Cross-origin handoff redirect has started; keep picker locked until navigation.
        return true;
      }

      if (statusOK(res)) {
        // Full session issuance makes the pre-auth picker timeout irrelevant.
        stopPickerTimer();
        sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
        onLoginAsSSOSuccess(account.name);
        return true;
      }

      if (isPickerActive.current) {
        setState({ isSubmitting: false, isAutoLoggingIn: false });
      }

      // 401 means the picker token has expired (5-minute TTL). Redirect the user
      // back to the login page so they can restart the SSO flow rather than leaving
      // them stuck on the picker with no explanation.
      if (res?.status === 401) {
        handlePickerSessionExpired();
        return false;
      }

      ErrorHelpers.handleError(
        'SSO Login Error',
        ErrorHelpers.createSimpleError('Unable to complete sign in. Please try again.'),
      );
      return false;
    } catch (error) {
      // Canceled requests are expected during navigation/unmount.
      if (axios.isCancel(error)) {
        return false;
      }

      if (!isPickerActive.current || requestID !== latestLoginRequestID.current) {
        return false;
      }

      setState({ isSubmitting: false, isAutoLoggingIn: false });

      if (error?.response?.status === 401) {
        handlePickerSessionExpired();
        return false;
      }

      ErrorHelpers.handleError(
        'SSO Login Error',
        ErrorHelpers.createSimpleError(
          'If this problem persists, please contact technical support.',
        ),
      );
      return false;
    } finally {
      if (cancelLoginRequest.current === source.cancel) {
        cancelLoginRequest.current = null;
      }
    }
  };

  /**
   * Starts login for the clicked row.
   * Selection auditing is recorded server-side in /authn/sso/login so
   * cross-origin handoff does not lose buffered client-only events.
   *
   * @param {React.MouseEvent} e Button click event.
   * @param {Object} account Selected normalized account row.
   * @returns {Promise<void>} Resolves after login attempt.
   */
  const handleLoginAsUser = async (e, account) => {
    e.stopPropagation();
    await loginAsSSOAccount(account, false);
  };

  /**
   * Clears temporary SSO picker token and returns user to login page.
   */
  const handleBack = () => {
    stopPickerTimer();

    // Ignore any in-flight account requests after user leaves the picker.
    latestRequestID.current += 1;
    latestLoginRequestID.current += 1;
    if (cancelLoginRequest.current) {
      cancelLoginRequest.current('SSO account login request was canceled');
      cancelLoginRequest.current = null;
    }
    sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
    history.push({ pathname: '/' });
  };

  const filteredAccounts = getFilteredAccounts();
  const tableColumnCount = SSO_COLUMNS.length + 1;
  const isAccountSelectionDisabled =
    state.isSubmitting || state.isLoading || state.isSearchLoading || state.isSearchDebouncing;

  // Show centered loading spinner while fetching initial accounts (no UI yet)
  if (!state.isAccountsDataLoaded) {
    return (
      <Spinner
        size={100}
        spinnerColor="blue"
        loadingText={SPINNER_DIALOGS.LOADING}
        textColor="white"
        bgColor="grey"
        lockActions={true}
        skipDelay
      />
    );
  }

  // Show pure centered loading spinner for single-account auto-login
  if (state.isAutoLoggingIn) {
    return (
      <Spinner
        size={100}
        spinnerColor="blue"
        loadingText="Login found, automatically logging you in..."
        textColor="white"
        bgColor="grey"
        lockActions={true}
        skipDelay
      />
    );
  }

  return (
    <div id="ssoAccountPickerContainer" className={classes.container}>
      <Paper className={classes.paper} elevation={2}>
        <div className={classes.titleRow}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Typography className={classes.title}>Select User Log In</Typography>
            </Grid>
            <Grid item>
              <FormControl>
                <TextField
                  id="txtSSOSearch"
                  variant="outlined"
                  placeholder="Search by User Name, Office or Company"
                  value={state.searchInput}
                  onChange={handleSearchChange}
                  className={classes.searchField}
                  InputProps={{
                    classes: { adornedStart: classes.searchAdornedStart },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="disabled" />
                      </InputAdornment>
                    ),
                    endAdornment:
                      state.isSearchDebouncing || state.isSearchLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : null,
                  }}
                  inputProps={{ 'aria-label': 'search accounts', className: classes.searchInput }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>

        <div className={classes.tableHeaderRegion}>
          <Table className={classes.fixedTableLayout}>
            {renderSSOAccountPickerColumnGroup()}
            <TableHead>
              <TableRow>
                {SSO_COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    component="th"
                    scope="col"
                    classes={{
                      root:
                        col.key === 'last_used'
                          ? `${classes.headerCell} ${classes.lastUsedCell}`
                          : classes.headerCell,
                    }}
                    sortDirection={
                      state.sortKey === col.key ? (state.isSortAsc ? 'asc' : 'desc') : false
                    }
                  >
                    <TableSortLabel
                      active={state.sortKey === col.key}
                      direction={
                        state.sortKey === col.key ? (state.isSortAsc ? 'asc' : 'desc') : 'desc'
                      }
                      onClick={() => handleSort(col.key)}
                      classes={{ icon: classes.sortLabelIcon }}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  component="th"
                  scope="col"
                  classes={{ root: classes.actionCell }}
                  padding="none"
                />
              </TableRow>
            </TableHead>
          </Table>
        </div>

        <div className={classes.tableScrollRegion}>
          <Table className={classes.fixedTableLayout}>
            {renderSSOAccountPickerColumnGroup()}
            {/* Hidden header row keeps body-cell header associations in this same table. */}
            <TableHead className={classes.srOnlyTableHead}>
              <TableRow>
                {SSO_COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    id={SSO_TABLE_HEADER_IDS[col.key]}
                    component="th"
                    scope="col"
                  >
                    {col.label}
                  </TableCell>
                ))}
                <TableCell id={SSO_TABLE_HEADER_IDS.action} component="th" scope="col" />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.isLoading ? (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} classes={{ root: classes.loadingState }}>
                    <CircularProgress />
                    <Typography className={classes.loadingText}>
                      Loading your SSO accounts...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} classes={{ root: classes.loadingState }}>
                    <Typography className={classes.loadingText}>
                      {state.searchInput ? 'No accounts match your search.' : 'No accounts found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAccounts.map(account => {
                  return (
                    <TableRow
                      key={account.id}
                      hover
                      className={classes.tableRow}
                      id={`rowSSOAccount_${account.id}`}
                    >
                      <TableCell headers={SSO_TABLE_HEADER_IDS.name}>{account.name}</TableCell>
                      <TableCell headers={SSO_TABLE_HEADER_IDS.role}>{account.role}</TableCell>
                      <TableCell
                        headers={SSO_TABLE_HEADER_IDS.office}
                        className={classes.officeCompanyCell}
                        title={account.office || ''}
                      >
                        {account.office}
                      </TableCell>
                      <TableCell headers={SSO_TABLE_HEADER_IDS.user_id}>
                        {account.user_id}
                      </TableCell>
                      <TableCell headers={SSO_TABLE_HEADER_IDS.account_code}>
                        {account.account_code}
                      </TableCell>
                      <TableCell headers={SSO_TABLE_HEADER_IDS.efin}>{account.efin}</TableCell>
                      <TableCell
                        headers={SSO_TABLE_HEADER_IDS.last_used}
                        className={classes.lastUsedCell}
                      >
                        {account.last_used}
                      </TableCell>
                      <TableCell
                        headers={SSO_TABLE_HEADER_IDS.action}
                        classes={{ root: classes.actionCell }}
                        padding="none"
                      >
                        <Button
                          id={`btnSSOLogin_${account.id}`}
                          variant="outlined"
                          color="primary"
                          size="small"
                          className={classes.loginAsUserButton}
                          disabled={isAccountSelectionDisabled}
                          onClick={e => handleLoginAsUser(e, account)}
                        >
                          Login as User
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          component="div"
          count={state.totalRows}
          rowsPerPage={state.rowsPerPage}
          page={state.page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={SSO_ACCOUNT_PAGE_SIZE_OPTIONS}
        />

        {/* Keep the expiry hint near footer actions so it stays visible but low emphasis. */}
        {sessionSecondsRemaining != null ? (
          <div className={classes.sessionHintRow}>
            <Typography className={classes.sessionHintText}>
              Session expires in {formatSessionCountdown(sessionSecondsRemaining)}
            </Typography>
          </div>
        ) : null}

        <Button
          id="lnkBackSSOPicker"
          variant="text"
          color="primary"
          className={classes.backButton}
          disabled={state.isSubmitting}
          onClick={handleBack}
        >
          &#8592; Back to Log In
        </Button>
      </Paper>

      {state.isSubmitting ? (
        <Spinner
          size={100}
          spinnerColor="blue"
          loadingText={SPINNER_DIALOGS.LOADING}
          textColor="white"
          bgColor="grey"
          lockActions={true}
          skipDelay
        />
      ) : null}
    </div>
  );
};

export default withRouter(withStyles(styles)(SSOAccountPicker));
